import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import "./file-upload-dropzone.scss";
import { getIconBasedOnFileExt } from "../../../utils/fetchIconForExtension";

export type UploadFile = {
  id: string;
  file: File;
  name: string;
  size: string;
  progress: number;
  status: "pending" | "uploading" | "done" | "error";
};

type Props = {
  files: UploadFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  errorMsg: Record<string, string>; // key is file ID
  setErrorMsg: React.Dispatch<React.SetStateAction<Record<string, string>>>;
};

export const ALLOWED_MIME_TYPES = [
  "application/pdf", // .pdf
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/msword", // .doc
  "image/png", // .png
  "image/jpg", // .jpg (non-standard but included)
  "image/jpeg", // .jpeg
  "text/plain", // .txt
  "text/csv", // .csv
  "video/mp4", // .mp4
];

const FileUploader: React.FC<Props> = ({
  files,
  setFiles,
  errorMsg,
  setErrorMsg,
}) => {
  const MAX_FILE_SIZE_MB = 110;
  const MAX_FILE_COUNT = 5;
  const MAX_FILE_ERROR_KEY = "__max_limit__";

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles: UploadFile[] = [];
      const newErrors: Record<string, string> = {};
      if (files.length + acceptedFiles.length > MAX_FILE_COUNT) {
        newErrors[MAX_FILE_ERROR_KEY] =
          `You can upload a maximum of ${MAX_FILE_COUNT} files.`;
      }
      acceptedFiles.forEach((file) => {
        const id = uuidv4();
        const fileSizeMB = file.size / (1024 * 1024);

        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
          newErrors[id] = `File "${file.name}" is not a supported file type.`;
        }

        if (fileSizeMB > MAX_FILE_SIZE_MB) {
          newErrors[id] = `File "${file.name}" exceeds the 100MB size limit.`;
        }

        newFiles.push({
          id,
          file,
          name: file.name,
          size: (file.size / 1024).toFixed(1),
          progress: 0,
          status: "pending",
        });
      });

      if (Object.keys(newErrors).length > 0) {
        setErrorMsg((prev) => ({ ...prev, ...newErrors }));
      }

      if (newFiles.length > 0) {
        setFiles((prev) => [...prev, ...newFiles]);
      }
    },
    [files, setFiles, setErrorMsg]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
  });

  const removeFile = (idToRemove: string) => {
    const newFiles = files.filter((f) => f.id !== idToRemove);
    setFiles(newFiles);
    setErrorMsg((prev) => {
      const newErrors = { ...prev };
      delete newErrors[idToRemove];
      if (newErrors[MAX_FILE_ERROR_KEY] && newFiles.length <= MAX_FILE_COUNT) {
        delete newErrors[MAX_FILE_ERROR_KEY];
      }
      return newErrors;
    });
  };
  const getFileIcon = (fileName: string) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    return getIconBasedOnFileExt(ext || "");
  };
  return (
    <div className="upload-file-wrapper">
      <div className="dropzone" {...getRootProps()}>
        <input {...getInputProps()} />
        Click here or drop files here to attach.
      </div>

      {files.map((f) => (
        <React.Fragment key={f?.id}>
          <div className="file-info-wrapper">
            <div title={f.name} className="file-name">
              <span className="file-type-icon">{getFileIcon(f.name)}</span>
              <span>
                {f.name} ({f.size} KB)
              </span>
            </div>
            <span onClick={() => removeFile(f?.id)} className="remove-icon">
              ×
            </span>
          </div>
          {errorMsg[f.id] && (
            <div className="error-message">{errorMsg[f.id]}</div>
          )}
        </React.Fragment>
      ))}
      {errorMsg[MAX_FILE_ERROR_KEY] && (
        <div className="error-message">{errorMsg[MAX_FILE_ERROR_KEY]}</div>
      )}
    </div>
  );
};

export default FileUploader;
